import { z } from 'zod';
import { isoDateSchema, uuidSchema } from './common.js';

/**
 * Query parameters for listing audit-log entries. All filters are optional;
 * RBAC scoping is enforced at the service layer (DATA_ENTRY operators only
 * ever see their own rows, regardless of these filters).
 */
export const auditListQuerySchema = z.object({
  /** Filter to actions performed by this user. */
  userId: uuidSchema.optional(),
  /** Exact action code, e.g. "TRANSACTION_CREATE". */
  action: z
    .string()
    .min(1, 'Action filter cannot be empty')
    .max(64, 'Action filter must be at most 64 characters')
    .optional(),
  /** Entity type, e.g. "Transaction", "Fund". */
  entityType: z
    .string()
    .min(1, 'Entity type filter cannot be empty')
    .max(64, 'Entity type filter must be at most 64 characters')
    .optional(),
  /** Filter to actions touching this entity id (any type). */
  entityId: z
    .string()
    .min(1, 'Entity ID filter cannot be empty')
    .max(128, 'Entity ID filter must be at most 128 characters')
    .optional(),
  /** Inclusive lower bound on the Dhaka calendar date of the entry. */
  dateFrom: isoDateSchema.optional(),
  /** Inclusive upper bound on the Dhaka calendar date of the entry. */
  dateTo: isoDateSchema.optional(),
  page: z.coerce.number().int().min(1, 'Page must be 1 or higher').default(1),
  limit: z.coerce
    .number()
    .int()
    .min(1, 'Limit must be 1 or higher')
    .max(200, 'Limit cannot exceed 200')
    .default(50),
});
export type AuditListQuery = z.infer<typeof auditListQuerySchema>;

/**
 * Frontend filter-form shape for the Audit Log page. All-string with empty
 * defaults so each field round-trips cleanly through URL search params via
 * `useUrlFilters`. The backend list endpoint uses `auditListQuerySchema`
 * (above) — same fields, but omits empties and validates types strictly.
 *
 * No `.uuid()` constraint on `userId` / `entityId` here: a half-typed UUID
 * in the input box must not flip the URL parse to "invalid" and lose the
 * user's other filters. The backend will reject a malformed UUID with a
 * proper field-level error when Apply is pressed.
 */
export const auditFilterFormSchema = z.object({
  action: z.string().default(''),
  entityType: z.string().default(''),
  entityId: z.string().default(''),
  userId: z.string().default(''),
  dateFrom: z.string().default(''),
  dateTo: z.string().default(''),
});
export type AuditFilterForm = z.infer<typeof auditFilterFormSchema>;

/**
 * Serialised audit-log row. `beforeValue` / `afterValue` retain whatever
 * shape was passed to `writeAudit` (already PII-stripped by the call site
 * — the audit log itself does not strip).
 */
export interface AuditLogResponse {
  id: string;
  userId: string | null;
  username: string | null;
  userName: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  beforeValue: unknown;
  afterValue: unknown;
  ipAddress: string | null;
  userAgent: string | null;
  requestId: string | null;
  timestamp: string;
}
